/**
 * Email Block Registry — defines all available email block types,
 * their defaults, categories, icons, and property schemas.
 *
 * Follows the same pattern as sites/blocks/registry.js
 */

export const EMAIL_BLOCK_CATEGORIES = [
  { id: 'content', label: 'Content', icon: 'Type' },
  { id: 'media', label: 'Media', icon: 'Image' },
  { id: 'layout', label: 'Layout', icon: 'LayoutGrid' },
  { id: 'structure', label: 'Structure', icon: 'PanelTop' },
];

export const EMAIL_BLOCK_REGISTRY = {
  text: {
    label: 'Text',
    icon: 'Type',
    category: 'content',
    description: 'Rich text with formatting',
    defaultProps: {
      content: '<p>Start typing...</p>',
      padding: '16px 24px',
      text_color: '#1c1917',
      background_color: 'transparent',
      font_size: '14px',
      line_height: '1.6',
    },
  },
  image: {
    label: 'Image',
    icon: 'ImageIcon',
    category: 'media',
    description: 'Image with optional caption & link',
    defaultProps: {
      image_url: '',
      alt_text: '',
      caption: '',
      link_url: '',
      width: '100%',
      alignment: 'center',
      border_radius: '0px',
      padding: '8px 24px',
    },
  },
  button: {
    label: 'Button',
    icon: 'MousePointerClick',
    category: 'content',
    description: 'Call-to-action button',
    defaultProps: {
      text: 'Click Here',
      href: '#',
      bg_color: '#1c1917',
      text_color: '#ffffff',
      border_radius: '6px',
      alignment: 'center',
      width: 'auto',
      font_size: '14px',
      padding_x: '24px',
      padding_y: '12px',
      outer_padding: '16px 24px',
    },
  },
  divider: {
    label: 'Divider',
    icon: 'Minus',
    category: 'layout',
    description: 'Horizontal line separator',
    defaultProps: {
      color: '#d6d3d1',
      thickness: '1px',
      width: '100%',
      padding: '16px 24px',
    },
  },
  columns: {
    label: 'Columns',
    icon: 'Columns2',
    category: 'layout',
    description: '2 or 3 column layout',
    defaultProps: {
      column_count: 2,
      column_ratio: '50-50',
      gap: '16px',
      padding: '8px 24px',
      columns: [
        { blocks: [{ block_id: '', block_type: 'text', props: { content: '<p>Column 1</p>', padding: '8px', text_color: '#1c1917', background_color: 'transparent', font_size: '14px', line_height: '1.6' } }] },
        { blocks: [{ block_id: '', block_type: 'text', props: { content: '<p>Column 2</p>', padding: '8px', text_color: '#1c1917', background_color: 'transparent', font_size: '14px', line_height: '1.6' } }] },
      ],
    },
  },
  spacer: {
    label: 'Spacer',
    icon: 'Space',
    category: 'layout',
    description: 'Vertical spacing',
    defaultProps: {
      height: '24px',
    },
  },
  social: {
    label: 'Social Links',
    icon: 'Share2',
    category: 'content',
    description: 'Social media icon links',
    defaultProps: {
      icons: [
        { platform: 'linkedin', url: '' },
        { platform: 'twitter', url: '' },
        { platform: 'facebook', url: '' },
        { platform: 'instagram', url: '' },
      ],
      alignment: 'center',
      icon_size: '24px',
      icon_style: 'color', // 'color' | 'dark' | 'light'
      spacing: '12px',
      padding: '16px 24px',
    },
  },
  header: {
    label: 'Header',
    icon: 'PanelTop',
    category: 'structure',
    description: 'Logo and preheader text',
    defaultProps: {
      logo_url: '',
      logo_alt: '',
      logo_width: '150px',
      logo_alignment: 'center',
      preheader_text: '',
      background_color: '#ffffff',
      padding: '24px',
    },
  },
  footer: {
    label: 'Footer',
    icon: 'PanelBottom',
    category: 'structure',
    description: 'Unsubscribe & company info',
    defaultProps: {
      company_name: '',
      address: '',
      unsubscribe_text: 'Unsubscribe',
      unsubscribe_url: '{{UnsubscribeUrl}}',
      text_color: '#78716c',
      background_color: '#f5f5f4',
      font_size: '11px',
      padding: '24px',
      show_powered_by: false,
    },
  },
  signature: {
    label: 'Signature',
    icon: 'FileSignature',
    category: 'structure',
    description: 'Email signature block',
    defaultProps: {
      signature_id: '',
      signature_html: '',
      padding: '16px 24px',
    },
  },
};

/**
 * Generate a new block object with a unique ID, the given type,
 * and a deep copy of default props.
 */
export function createBlock(blockType, initialProps = null) {
  const definition = EMAIL_BLOCK_REGISTRY[blockType];
  if (!definition) throw new Error(`Unknown email block type: ${blockType}`);

  const props = { ...JSON.parse(JSON.stringify(definition.defaultProps)), ...(initialProps || {}) };

  // Generate IDs for nested column blocks
  if (blockType === 'columns' && props.columns) {
    props.columns.forEach(col => {
      col.blocks.forEach(b => { b.block_id = 'blk_' + Math.random().toString(36).slice(2, 10); });
    });
  }

  return {
    block_id: 'blk_' + Math.random().toString(36).slice(2, 10),
    block_type: blockType,
    props,
  };
}

/**
 * Default email settings
 */
export const DEFAULT_EMAIL_SETTINGS = {
  max_width: 600,
  background_color: '#f5f5f4',
  content_background_color: '#ffffff',
  font_family: 'Arial, Helvetica, sans-serif',
};

/**
 * Starter template — blocks to initialize a new email with
 */
export function getStarterBlocks() {
  return [
    createBlock('text'),
    createBlock('signature'),
  ];
}
